import { BroomIcon, FeedbackIcon, HistoryIcon, KeyIcon, LogIcon, ReceiptIcon, StoreIcon, UserIcon, UsersIcon } from '@/components/icons'
import type { System } from '@/lib/auth-store'
import type { NavItem } from '@/lib/nav'

/*
 * Registry order is priority — how often a phone check opens each page: past 4 items the
 * bottom bar keeps the first 3 and moves the rest under More (T13). A new page slots in by
 * that rule, with its `group` and, when role-gated, a `visible` filter.
 */

// The account pages (2026-09-12): the only nav on the main host; a restaurant's manage
// pages carry their own (`restaurantNav`), and these stay reachable from the avatar menu.
const RESTAURANT_NAV: readonly NavItem<'restaurant'>[] = [
  { key: 'profile', to: '/app/profile', icon: UserIcon, label: 'profile.title' },
  { key: 'my-orders', to: '/app/my-orders', icon: ReceiptIcon, label: 'myOrders.title' },
  { key: 'restaurants', to: '/app/restaurants', icon: StoreIcon, label: 'nav.myRestaurants' },
]

const PLATFORM_NAV: readonly NavItem<'platform'>[] = [
  { key: 'restaurants', to: '/restaurants', icon: StoreIcon, label: 'nav.restaurants', group: 'platform' },
  // H4: every platform user reads the log; finer platform roles are future work.
  { key: 'system-log', to: '/system-log', icon: LogIcon, label: 'nav.systemLog', group: 'platform' },
  // E5: every platform user handles the manual OTP queue.
  { key: 'otp', to: '/otp-requests', icon: KeyIcon, label: 'nav.otp', group: 'platform' },
  // E4: admin-only; `RequireAdmin` covers a typed URL.
  {
    key: 'users',
    to: '/users',
    icon: UsersIcon,
    label: 'nav.users',
    group: 'platform',
    visible: (user) => user.is_admin,
  },
  // I2: what platform users did — admin-only.
  {
    key: 'audit-log',
    to: '/audit-log',
    icon: HistoryIcon,
    label: 'nav.auditLog',
    group: 'platform',
    visible: (user) => user.is_admin,
  },
  // I3: what restaurant users say about Romi; every platform user reads it.
  { key: 'feedback', to: '/feedback', icon: FeedbackIcon, label: 'nav.feedback', group: 'platform' },
  // H7: deletes across every restaurant — admin-only, and last: destructive and rare.
  {
    key: 'cleanup',
    to: '/cleanup',
    icon: BroomIcon,
    label: 'nav.cleanup',
    group: 'platform',
    visible: (user) => user.is_admin,
  },
]

export function navFor<S extends System>(system: S): readonly NavItem<S>[] {
  return (system === 'platform' ? PLATFORM_NAV : RESTAURANT_NAV) as readonly NavItem<S>[]
}
